import React, { useEffect, useMemo, useState } from 'react';

import { InfoCircleIcon } from '@vlabs/icons';
import { PageHeader } from '@vlabs/shared/components/page-header/PageHeader';
import { routes } from '@vlabs/shared/navigation/routes';
import {
  Breadcrumbs, Breadcrumb, ProgressBar, Pages,
} from '@vlabs/uikit';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { useParams, generatePath, useLocation } from 'react-router-dom';

import {
  LivenessSection, DeepfakeSection, FaceSection, BodySection,
} from './sections';
import { useTaskData } from './useTaskData';
import st from './VideoChecksResultPage.module.sass';
import { ResultPanel } from '../../components';
import { PersonSelector } from '../../components/person-selector/PersonSelector';
import { formatTime } from '../../utils/formatTime';
import { VideoChecksProcessingVideo } from '../video-checks/video-checks-processing-video/VideoChecksProcessingVideo';

function Hint({ children }) {
  return (
    <div className={st.Hint}>
      <InfoCircleIcon className={st.HintIcon} />
      {children}
    </div>
  );
}

function getNearestFrameEstimation(frames, time) {
  if (!frames || frames.length === 0) return null;
  if (frames.length === 1) return frames[0];

  let left = 0;
  let right = frames.length - 1;

  if (time <= frames[0].time_offset) return frames[0];
  if (time >= frames[right].time_offset) return frames[right];

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midTime = frames[mid].time_offset;

    if (midTime === time) {
      return frames[mid];
    }

    if (midTime < time) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  const before = frames[right];
  const after = frames[left];

  return (
    Math.abs(before.time_offset - time) <= Math.abs(after.time_offset - time)
      ? before
      : after
  );
}

const getPersonsByTime = (persons, currentTime) => {
  if (!Array.isArray(persons)) return [];
  return persons.filter((item) => currentTime >= item.segmentStartTime && currentTime <= item.segmentEndTime);
};

export function VideoChecksResultPage() {
  const { t } = useTranslation();
  const { taskId } = useParams();
  const { search } = useLocation();
  const {
    state: taskState,
    taskProgress,
    data,
  } = useTaskData();
  const [currentTime, setCurrentTime] = useState(0);
  const [visiblePersonList, setVisiblePersonList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [hoveredEventId, setHoveredEventId] = useState(null);

  const queryParams = useMemo(() => qs.parse(search, { ignoreQueryPrefix: true }), [search]);

  useEffect(() => {
    return () => {
      setCurrentTime(0);
      setVisiblePersonList([]);
      setSelectedEventId(null);
    };
  }, [queryParams, taskId]);

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('checks:check page title')}
        href={generatePath(routes.checks.read)}
      />
      <Breadcrumb caption={t('checks:check type card.video.title')} />
    </Breadcrumbs>
  );

  const selectedEvent = useMemo(() => {
    if (selectedEventId === null || !data) return null;

    const events = data?.events || [];
    const currentEvent = events.find((e) => String(e.event_id) === selectedEventId);
    if (!currentEvent) return null;

    const { frames_estimations, ...rest } = currentEvent;
    const nearestFrame = getNearestFrameEstimation(frames_estimations, currentTime);

    return {
      ...rest,
      eventId: currentEvent.event_id,
      frameEstimations: nearestFrame,
      frames: [
        {
          image: currentEvent.overview.image,
          bbox: currentEvent.overview.detection.face?.bbox,
        },
      ],
    };
  }, [selectedEventId, currentTime, data]);

  const handleEventSelect = (id) => {
    setSelectedEventId((prev) => (prev === id ? null : id));
  };

  const onPauseVideo = () => {
    setSelectedEventId(null);
    setHoveredEventId(null);
    setVisiblePersonList(getPersonsByTime(data.personList, currentTime));
  };

  const onPlayVideo = () => {
    setSelectedEventId(null);
    setHoveredEventId(null);
    setVisiblePersonList([]);
  };

  if (taskProgress?.status === 'in_progress') {
    return (
      <div className={st.Root}>
        <div className={st.EmptyPanel}>
          <div className={st.ProgressBar}>
            <span>{t('checks:loading text')}</span>
            <ProgressBar
              progress={taskProgress?.progress}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {!!data && (
        <React.Fragment>
          <div className={st.Content}>
            <PageHeader
              breadcrumbs={breadcrumbs}
              title={t('checks:check page subtitle')}
            />
            <VideoChecksProcessingVideo
              currentTime={currentTime}
              hoveredEventId={hoveredEventId}
              onEventIdClick={handleEventSelect}
              onEventHover={setHoveredEventId}
              onPauseVideo={onPauseVideo}
              onPlayVideo={onPlayVideo}
              onTimeChange={setCurrentTime}
              result={data}
              selectedEventId={selectedEventId}
            />
          </div>

          <ResultPanel
            className={st.ResultPanel}
            frameTime={formatTime(currentTime)}
            sourceFileName={queryParams?.filename}
          >
            {visiblePersonList?.length === 0 && !selectedEvent && (
              <Hint>
                {t('checks:result panel.hint.pause')}
              </Hint>
            )}
            {visiblePersonList?.length > 0
              && (
                <div className={st.PersonSelector}>
                  <span className={st.Subtitle}>{`${t('checks:result panel.results.detected people')} (${visiblePersonList?.length})`}</span>
                  <PersonSelector
                    hoveredId={hoveredEventId}
                    onHover={setHoveredEventId}
                    onSelect={handleEventSelect}
                    personList={visiblePersonList}
                    selectedId={selectedEventId}
                  />
                </div>
              )}

            {visiblePersonList?.length > 0 && !selectedEvent && (
              <Hint>{t('checks:result panel.hint.select person')}</Hint>
            )}
            {!!selectedEvent && (
              <React.Fragment>
                <LivenessSection value={selectedEvent?.aggregated_estimations?.face?.liveness} />
                <DeepfakeSection
                  eventId={selectedEvent?.eventId}
                  frames={selectedEvent?.frames}
                  value={selectedEvent?.aggregated_estimations?.face?.deepfake}
                />
                <FaceSection value={selectedEvent} />
                <BodySection value={selectedEvent} />
              </React.Fragment>
            )}
          </ResultPanel>
        </React.Fragment>
      )}

      {taskState === 'failed' && <Pages.NotFoundPage />}
    </div>
  );
}
